import React, { useState, useEffect } from 'react';
// import './App.css'; // Assuming basic styles are here or Tailwind is set up

// Material properties for common spring materials
interface MaterialProperty {
    name: string;
    shearModulus: number; // MPa
    allowableShearStress: number; // MPa
}

const materialProperties: Record<string, MaterialProperty> = {
    'music_wire': {
        name: 'Music Wire (ASTM A228)',
        shearModulus: 79300,
        allowableShearStress: 690
    },
    'hard_drawn': {
        name: 'Hard Drawn Wire (ASTM A227)',
        shearModulus: 79300,
        allowableShearStress: 550
    },
    'chrome_vanadium': {
        name: 'Chrome Vanadium (ASTM A232)',
        shearModulus: 79300,
        allowableShearStress: 690
    },
    'stainless_302': {
        name: 'Stainless Steel 302 (ASTM A313)',
        shearModulus: 69000,
        allowableShearStress: 520
    },
    'stainless_316': {
        name: 'Stainless Steel 316 (ASTM A313)',
        shearModulus: 69000,
        allowableShearStress: 520
    },
    'phosphor_bronze': {
        name: 'Phosphor Bronze (ASTM B159)',
        shearModulus: 41400,
        allowableShearStress: 380
    },
    'beryllium_copper': {
        name: 'Beryllium Copper (ASTM B197)',
        shearModulus: 48300,
        allowableShearStress: 480
    }
};

type UnitSystem = 'metric' | 'imperial';

interface SpringInputs {
    wireDiameter: string;
    outerDiameter: string;
    freeLength: string;
    totalCoils: string;
    materialType: string;
    endType: 'squared_ground' | 'squared_only' | 'plain_ends' | 'plain_ground';
}

interface SpringOutputs {
    // Calculated Diameters
    meanDiameter?: number;
    innerDiameter?: number;

    // Basic Spring Parameters
    springIndex?: number;
    activeCoils?: number;
    springRate?: number;
    solidHeight?: number;

    // Performance at Solid Height
    deflectionToSolid?: number;
    forceAtSolid?: number;
    wahlFactor?: number;
    stressAtSolid?: number;
    factorOfSafety?: number;

    // Other Geometric Properties
    pitch?: number;
    helixAngle?: number;

    errorMessages: string[];
}

// Conversion factors
const MM_TO_INCH = 0.0393701;
const INCH_TO_MM = 25.4;
const N_TO_LBF = 0.224809;
const N_PER_MM_TO_LBF_PER_INCH = 5.71015;
const MPA_TO_KSI = 0.145038;

const TestSpring: React.FC = () => {
    const [unitSystem, setUnitSystem] = useState<UnitSystem>('metric');
    const [inputs, setInputs] = useState<SpringInputs>({
        wireDiameter: '2', // mm or inch
        outerDiameter: '20', // mm or inch
        freeLength: '50', // mm or inch
        totalCoils: '12',
        materialType: 'music_wire',
        endType: 'squared_ground',
    });

    const [outputs, setOutputs] = useState<SpringOutputs>({ errorMessages: [] });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setInputs(prev => ({ ...prev, [name]: value }));
    };

    const handleUnitSystemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newUnitSystem = e.target.value as UnitSystem;

        if (newUnitSystem !== unitSystem) {
            // Convert input values when changing unit system
            const convertedInputs = { ...inputs };

            if (newUnitSystem === 'imperial') {
                // Convert from metric to imperial
                convertedInputs.wireDiameter = (parseFloat(inputs.wireDiameter) * MM_TO_INCH).toFixed(4);
                convertedInputs.outerDiameter = (parseFloat(inputs.outerDiameter) * MM_TO_INCH).toFixed(4);
                convertedInputs.freeLength = (parseFloat(inputs.freeLength) * MM_TO_INCH).toFixed(4);
            } else {
                // Convert from imperial to metric
                convertedInputs.wireDiameter = (parseFloat(inputs.wireDiameter) * INCH_TO_MM).toFixed(2);
                convertedInputs.outerDiameter = (parseFloat(inputs.outerDiameter) * INCH_TO_MM).toFixed(2);
                convertedInputs.freeLength = (parseFloat(inputs.freeLength) * INCH_TO_MM).toFixed(2);
            }

            setInputs(convertedInputs);
            setUnitSystem(newUnitSystem);
        }
    };

    useEffect(() => {
        calculateOutputs();
    }, [inputs, unitSystem]);

    const calculateOutputs = () => {
        const newOutputs: SpringOutputs = { errorMessages: [] };
        const errors: string[] = [];

        // Parse inputs to numbers, handle potential NaN
        const d = parseFloat(inputs.wireDiameter);
        const D_outer = parseFloat(inputs.outerDiameter);
        const L_free = parseFloat(inputs.freeLength);
        const n_t = parseFloat(inputs.totalCoils);

        // Get material properties
        const material = materialProperties[inputs.materialType];
        if (!material) {
            errors.push('Invalid material type selected.');
            newOutputs.errorMessages = errors;
            setOutputs(newOutputs);
            return;
        }

        // Get material properties and convert if necessary
        const G = material.shearModulus; // MPa
        const allowableStress = material.allowableShearStress; // MPa

        // Convert inputs to metric for calculations if using imperial
        let d_calc = d;
        let D_outer_calc = D_outer;
        let L_free_calc = L_free;

        if (unitSystem === 'imperial') {
            d_calc = d * INCH_TO_MM;
            D_outer_calc = D_outer * INCH_TO_MM;
            L_free_calc = L_free * INCH_TO_MM;
        }

        // Validate basic inputs
        if (isNaN(d) || d <= 0) errors.push('Wire Diameter must be a positive number.');
        if (isNaN(D_outer) || D_outer <= 0) errors.push('Outside Diameter must be a positive number.');
        if (isNaN(L_free) || L_free <= 0) errors.push('Free Length must be a positive number.');
        if (isNaN(n_t) || n_t <= 0) errors.push('Total Coils must be a positive number.');

        // Calculate Mean Diameter (D)
        let D_calc: number | undefined;
        if (!isNaN(D_outer_calc) && !isNaN(d_calc) && D_outer_calc > d_calc) {
            D_calc = D_outer_calc - d_calc;

            // Convert back to imperial if needed for display
            if (unitSystem === 'imperial') {
                newOutputs.meanDiameter = D_calc * MM_TO_INCH;
                newOutputs.innerDiameter = (D_calc - d_calc) * MM_TO_INCH;
            } else {
                newOutputs.meanDiameter = D_calc;
                newOutputs.innerDiameter = D_calc - d_calc;
            }
        } else {
            errors.push('Outside Diameter must be greater than Wire Diameter.');
        }

        // Calculate Active Coils based on end type
        let n_a: number | undefined;
        switch (inputs.endType) {
            case 'squared_ground':
                n_a = n_t - 2;
                break;
            case 'squared_only':
                n_a = n_t - 2;
                break;
            case 'plain_ends':
                n_a = n_t;
                break;
            case 'plain_ground':
                n_a = n_t - 1;
                break;
            default:
                errors.push('Invalid end type.');
        }

        if (n_a !== undefined && n_a <= 0) {
            errors.push('Active coils calculated to be zero or negative. Increase total coils or change end type.');
        } else if (n_a !== undefined) {
            newOutputs.activeCoils = n_a;
        }

        if (errors.length > 0) {
            newOutputs.errorMessages = errors;
            setOutputs(newOutputs);
            return;
        }

        // All primary inputs (G, d_calc, D_calc, L_free_calc, n_t, n_a) should be valid numbers now.
        if (D_calc === undefined || isNaN(D_calc) || D_calc <= 0 || n_a === undefined || isNaN(n_a) || n_a <= 0) {
            newOutputs.errorMessages = errors.length > 0 ? errors : ['Critical input parameters are missing or invalid for further calculations.'];
            setOutputs(newOutputs);
            return;
        }

        // Spring Index (C) - dimensionless, no conversion needed
        const C = D_calc / d_calc;
        newOutputs.springIndex = C;

        // Spring Rate (k)
        const k_metric = (G * Math.pow(d_calc, 4)) / (8 * Math.pow(D_calc, 3) * n_a);

        if (unitSystem === 'imperial') {
            // Convert N/mm to lbf/in
            newOutputs.springRate = k_metric * N_PER_MM_TO_LBF_PER_INCH;
        } else {
            newOutputs.springRate = k_metric;
        }

        // Solid Height (L_solid)
        const L_solid_metric = n_t * d_calc;

        if (unitSystem === 'imperial') {
            newOutputs.solidHeight = L_solid_metric * MM_TO_INCH;
        } else {
            newOutputs.solidHeight = L_solid_metric;
        }

        if (L_solid_metric >= L_free_calc) {
            errors.push('Solid Height is greater than or equal to Free Length. Spring is not possible.');
        }

        // Performance at Solid Height
        if (L_solid_metric < L_free_calc) {
            const deflection_to_solid_metric = L_free_calc - L_solid_metric;

            if (unitSystem === 'imperial') {
                newOutputs.deflectionToSolid = deflection_to_solid_metric * MM_TO_INCH;
            } else {
                newOutputs.deflectionToSolid = deflection_to_solid_metric;
            }

            const F_solid_metric = k_metric * deflection_to_solid_metric;

            if (unitSystem === 'imperial') {
                newOutputs.forceAtSolid = F_solid_metric * N_TO_LBF;
            } else {
                newOutputs.forceAtSolid = F_solid_metric;
            }

            const K_w = (4 * C - 1) / (4 * C - 4) + (0.615 / C);
            newOutputs.wahlFactor = K_w;

            const tau_solid_metric = (8 * K_w * D_calc * F_solid_metric) / (Math.PI * Math.pow(d_calc, 3));

            if (unitSystem === 'imperial') {
                newOutputs.stressAtSolid = tau_solid_metric * MPA_TO_KSI;
            } else {
                newOutputs.stressAtSolid = tau_solid_metric;
            }

            if (allowableStress > 0) {
                newOutputs.factorOfSafety = allowableStress / tau_solid_metric;
            }
        } else {
            newOutputs.deflectionToSolid = 0;
            newOutputs.forceAtSolid = 0;
        }

        // Other Geometric Properties
        let pitch_val_metric: number | undefined;
        if (n_t > n_a && (L_free_calc - (n_t - n_a) * d_calc) > 0) {
            pitch_val_metric = (L_free_calc - (n_t - n_a) * d_calc) / n_a;
        } else if (n_t === n_a) { // E.g. plain ends
            pitch_val_metric = L_free_calc / n_a; // This is simplified, assumes coils are evenly spaced
        }

        if (pitch_val_metric !== undefined && pitch_val_metric > d_calc) { // Pitch must be greater than wire diameter
            if (unitSystem === 'imperial') {
                newOutputs.pitch = pitch_val_metric * MM_TO_INCH;
            } else {
                newOutputs.pitch = pitch_val_metric;
            }

            newOutputs.helixAngle = Math.atan(pitch_val_metric / (Math.PI * D_calc)) * (180 / Math.PI); // in degrees, no conversion needed
        } else if (pitch_val_metric !== undefined && pitch_val_metric <= d_calc) {
            errors.push('Calculated pitch is less than or equal to wire diameter. Spring coils would clash.');
        }

        newOutputs.errorMessages = errors;
        setOutputs(newOutputs);
    };

    const getUnitLabel = (baseUnit: string): string => {
        switch (baseUnit) {
            case 'mm':
                return unitSystem === 'metric' ? 'mm' : 'in';
            case 'N':
                return unitSystem === 'metric' ? 'N' : 'lbf';
            case 'N/mm':
                return unitSystem === 'metric' ? 'N/mm' : 'lbf/in';
            case 'MPa':
                return unitSystem === 'metric' ? 'MPa' : 'ksi';
            default:
                return baseUnit;
        }
    };

    const renderOutputField = (label: string, value?: number, baseUnit?: string, precision = 3) => {
        if (value === undefined || isNaN(value)) return null;
        const unit = baseUnit ? getUnitLabel(baseUnit) : '';
        return (
            <div className="grid grid-cols-2 gap-2">
                <span className="font-medium">{label}:</span>
                <span>{value.toFixed(precision)} {unit}</span>
            </div>
        );
    };

    return (
        <div className="container mx-auto p-4 font-sans">
            <header className="text-center mb-8">
                <h1 className="text-3xl font-bold text-blue-600">Compression Spring Design Calculator</h1>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Input Section */}
                <section className="p-6 bg-gray-50 rounded-lg shadow">
                    <h2 className="text-xl font-semibold mb-4 text-gray-700">Input Parameters</h2>

                    {/* Unit System Selector */}
                    <div className="mb-6 border-b pb-4">
                        <h3 className="text-lg font-medium mb-2 text-gray-600">Unit System</h3>
                        <div className="flex space-x-4">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="unitSystem"
                                    value="metric"
                                    checked={unitSystem === 'metric'}
                                    onChange={handleUnitSystemChange}
                                    className="form-radio h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2">Metric (mm, N)</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="unitSystem"
                                    value="imperial"
                                    checked={unitSystem === 'imperial'}
                                    onChange={handleUnitSystemChange}
                                    className="form-radio h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2">Imperial (inch, lbf)</span>
                            </label>
                        </div>
                    </div>

                    {/* Spring Geometry */}
                    <div className="mb-6 border-b pb-4">
                        <h3 className="text-lg font-medium mb-2 text-gray-600">Spring Geometry</h3>
                        <label className="block mb-2">
                            Wire Diameter ({getUnitLabel('mm')}):
                            <input type="number" name="wireDiameter" value={inputs.wireDiameter} onChange={handleInputChange} className="mt-1 p-2 w-full border rounded" placeholder={`e.g., ${unitSystem === 'metric' ? '2' : '0.079'}`} />
                        </label>

                        <label className="block mb-2">
                            Outside Diameter ({getUnitLabel('mm')}):
                            <input type="number" name="outerDiameter" value={inputs.outerDiameter} onChange={handleInputChange} className="mt-1 p-2 w-full border rounded" placeholder={`e.g., ${unitSystem === 'metric' ? '20' : '0.787'}`} />
                        </label>

                        <label className="block mb-2">
                            Free Length ({getUnitLabel('mm')}):
                            <input type="number" name="freeLength" value={inputs.freeLength} onChange={handleInputChange} className="mt-1 p-2 w-full border rounded" placeholder={`e.g., ${unitSystem === 'metric' ? '50' : '1.969'}`} />
                        </label>

                        <label className="block mb-2">
                            Total Coils:
                            <input type="number" name="totalCoils" value={inputs.totalCoils} onChange={handleInputChange} className="mt-1 p-2 w-full border rounded" placeholder="e.g., 12" />
                        </label>
                    </div>

                    {/* Material Properties */}
                    <div className="mb-6 border-b pb-4">
                        <h3 className="text-lg font-medium mb-2 text-gray-600">Material Properties</h3>
                        <label className="block mb-2">
                            Material Type:
                            <select name="materialType" value={inputs.materialType} onChange={handleInputChange} className="mt-1 p-2 w-full border rounded">
                                {Object.entries(materialProperties).map(([key, material]) => (
                                    <option key={key} value={key}>{material.name}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {/* End Type */}
                    <div>
                        <h3 className="text-lg font-medium mb-2 text-gray-600">End Type</h3>
                        <label className="block mb-2">
                            End Type:
                            <select name="endType" value={inputs.endType} onChange={handleInputChange} className="mt-1 p-2 w-full border rounded">
                                <option value="squared_ground">Squared & Ground</option>
                                <option value="squared_only">Squared Only</option>
                                <option value="plain_ends">Plain Ends</option>
                                <option value="plain_ground">Plain & Ground</option>
                            </select>
                        </label>
                    </div>
                </section>

                {/* Output Section */}
                <section className="p-6 bg-gray-50 rounded-lg shadow">
                    <h2 className="text-xl font-semibold mb-4 text-gray-700">Calculated Outputs</h2>
                    {outputs.errorMessages.length > 0 && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                            <h3 className="font-bold">Errors:</h3>
                            <ul className="list-disc list-inside">
                                {outputs.errorMessages.map((err, i) => <li key={i}>{err}</li>)}
                            </ul>
                        </div>
                    )}
                    {outputs.errorMessages.length === 0 && (
                        <div className="space-y-3">
                            <h3 className="text-lg font-medium text-gray-600 border-b pb-2">Calculated Diameters</h3>
                            {renderOutputField('Mean Diameter', outputs.meanDiameter, 'mm')}
                            {renderOutputField('Inner Diameter', outputs.innerDiameter, 'mm')}

                            <h3 className="text-lg font-medium text-gray-600 border-b pb-2 pt-3">Basic Spring Parameters</h3>
                            {renderOutputField('Spring Index (C)', outputs.springIndex, '', 2)}
                            {renderOutputField('Active Coils', outputs.activeCoils, 'coils', 1)}
                            {renderOutputField('Spring Rate (k)', outputs.springRate, 'N/mm')}
                            {renderOutputField('Solid Height', outputs.solidHeight, 'mm')}

                            <h3 className="text-lg font-medium text-gray-600 border-b pb-2 pt-3">Performance at Solid Height</h3>
                            {renderOutputField('Deflection to Solid', outputs.deflectionToSolid, 'mm')}
                            {renderOutputField('Force at Solid Height', outputs.forceAtSolid, 'N')}
                            {renderOutputField('Wahl Correction Factor', outputs.wahlFactor, '', 3)}
                            {renderOutputField('Stress at Solid Height', outputs.stressAtSolid, 'MPa')}
                            {renderOutputField('Factor of Safety', outputs.factorOfSafety, '', 2)}

                            <h3 className="text-lg font-medium text-gray-600 border-b pb-2 pt-3">Other Geometric Properties</h3>
                            {renderOutputField('Pitch', outputs.pitch, 'mm')}
                            {renderOutputField('Helix Angle', outputs.helixAngle, 'degrees', 2)}
                        </div>
                    )}
                </section>
            </div>

            <footer className="text-center mt-12 py-4 border-t">
                <p className="text-sm text-gray-600">Compression Spring Calculator | Updated Version</p>
                <p className="text-xs text-gray-500">Note: This calculator provides theoretical estimations. Always verify designs with physical testing and professional engineering consultation.</p>
            </footer>
        </div>
    )
}
export default TestSpring;
{/* // (Content truncated due to size limit. Use line ranges to read in chunks) */ }